import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import VotingABI from '../abi/Voting.json';
import CandidateImage from './CandidateImage';
import { parseContractError } from '../utils/parseError';

const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const SEPOLIA_RPC_URL  = process.env.REACT_APP_SEPOLIA_RPC_URL;
const BACKEND_URL      = process.env.REACT_APP_BACKEND_URL;

export default function P03CandidateReg({ isAdmin, onBackClick, onManageClick }) {
  const [name, setName]             = useState('');
  const [description, setDesc]      = useState('');
  const [imageFile, setImageFile]   = useState(null);
  const [imagePreview, setPreview]  = useState(null);
  const [isLoading, setLoading]     = useState(false);
  const [errorMsg, setErrorMsg]     = useState('');
  const [toast, setToast]           = useState('');
  const [candidates, setCandidates] = useState([]);

  useEffect(() => { if (!isAdmin) onBackClick(); }, [isAdmin, onBackClick]);
  useEffect(() => { loadCandidates(); }, []);

  async function loadCandidates() {
    try {
      const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
      const onchain = await contract.getCandidates();
      let offchain = [];
      try { offchain = await axios.get(`${BACKEND_URL}/candidates`).then(r => r.data); } catch {}
      const merged = onchain.map(c => {
        const off = offchain.find(o => o.id === Number(c.id)) || {};
        return { id: Number(c.id), name: c.name,
          imageUrl: off.imageUrl || null, description: off.description || '' };
      });
      setCandidates(merged);
    } catch (e) { console.error('후보자 목록 로딩 오류:', e); }
  }

  function onImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMsg(''); setToast('');
    const trimmedName = name.trim();
    if (!trimmedName) { setErrorMsg('이름을 입력해주세요.'); return; }
    if (candidates.some(c => c.name === trimmedName)) {
      setErrorMsg('이미 등록된 이름입니다.'); return;
    }
    let imageUrl = null;
    if (imageFile) {
      try {
        const formData = new FormData();
        formData.append('image', imageFile);
        const res = await axios.post(`${BACKEND_URL}/upload`, formData);
        imageUrl = res.data.imageUrl;
      } catch { setErrorMsg('이미지 업로드에 실패했습니다.'); return; }
    }
    setLoading(true);
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, signer);
      const tx = await contract.addCandidate(trimmedName);
      const receipt = await tx.wait();
      const iface = new ethers.Interface(VotingABI.abi);
      const eventLog = receipt.logs
        .map(log => { try { return iface.parseLog(log); } catch { return null; } })
        .find(log => log?.name === 'CandidateAdded');
      if (!eventLog) throw new Error('CandidateAdded 이벤트를 찾을 수 없습니다.');
      const candidateId = Number(eventLog.args.id);
      await axios.post(`${BACKEND_URL}/candidates`,
        { id: candidateId, name: trimmedName, description: description.trim(), imageUrl });
      setToast('후보자가 등록되었습니다.');
      setName(''); setDesc(''); setImageFile(null); setPreview(null);
      await loadCandidates();
    } catch (err) {
      const msg = parseContractError(err);
      setErrorMsg(msg || '등록 중 오류가 발생했습니다.');
      if (!msg) console.error(err);
    } finally { setLoading(false); }
  }

  return (
    <div className="ev-page" style={{ maxWidth: 720 }}>
      {isLoading && (
        <div className="ev-overlay">
          <div className="ev-spinner" />
          <p className="ev-overlay-text">블록체인에 기록 중... MetaMask에서 서명 후 잠시 기다려 주세요.</p>
        </div>
      )}

      <div className="ev-page-header">
        <h1 className="ev-page-title">후보자 등록</h1>
        <p className="ev-page-subtitle">새 후보자를 등록합니다. 이름은 블록체인에 저장됩니다.</p>
      </div>

      <div className="ev-card" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSubmit}>
          {errorMsg && <div className="ev-error">{errorMsg}</div>}
          {toast && <div className="ev-success">{toast}</div>}

          <div className="ev-form-group">
            <label className="ev-label">이름 * (1~30자)</label>
            <input className="ev-input" type="text" value={name}
              onChange={e => { setName(e.target.value); setErrorMsg(''); setToast(''); }}
              maxLength={30} disabled={isLoading} />
            <div className="ev-char-count">{name.length}/30</div>
          </div>

          <div className="ev-form-group">
            <label className="ev-label">사진 (선택, JPG/PNG, 5MB 이하)</label>
            <input type="file" accept="image/jpeg,image/png"
              onChange={onImageChange} disabled={isLoading}
              style={{ color: 'var(--text-sub)', fontSize: '0.85rem' }} />
            {imagePreview && (
              <img src={imagePreview} alt="미리보기"
                style={{ marginTop: 10, width: 80, height: 80, objectFit: 'cover',
                  borderRadius: '50%', border: '2px solid var(--border)' }} />
            )}
          </div>

          <div className="ev-form-group">
            <label className="ev-label">소개 (선택, 최대 200자)</label>
            <textarea className="ev-textarea" value={description}
              onChange={e => setDesc(e.target.value)}
              maxLength={200} disabled={isLoading} rows={3} />
            <div className="ev-char-count">{description.length}/200</div>
          </div>

          <div className="ev-btn-group">
            <button type="submit" className="ev-btn ev-btn-primary" disabled={isLoading}>
              등록하기 →
            </button>
          </div>
        </form>
      </div>

      <div className="ev-card">
        <div className="ev-reg-list-header">
          <span className="ev-reg-list-title">등록된 후보자 목록</span>
          <span className="ev-reg-count-badge">
            {candidates.length}명{candidates.length >= 2 ? ' ✓ 최소 조건 충족' : ''}
          </span>
        </div>
        <div className="ev-candidate-list">
          {candidates.length === 0 && (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-sub)', padding: '8px 0' }}>
              아직 등록된 후보자가 없습니다.
            </p>
          )}
          {candidates.map(c => (
            <div key={c.id} className="ev-candidate-row">
              <span className="ev-candidate-row__num">#{c.id}</span>
              <CandidateImage
                src={c.imageUrl ? BACKEND_URL + c.imageUrl : null}
                alt={c.name}
                circle
                width={36}
                height={36}
              />
              <span className="ev-candidate-row__name">{c.name}</span>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-sub)', flex: 1 }}>
                {c.description ? c.description.slice(0, 40) : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="ev-btn-group" style={{ marginTop: 20 }}>
        <button className="ev-btn ev-btn-ghost" onClick={onBackClick} disabled={isLoading}>← 뒤로</button>
        <button className="ev-btn ev-btn-primary" onClick={onManageClick} disabled={isLoading}>투표 관리 →</button>
      </div>
    </div>
  );
}
